import React, { useMemo, Fragment, useState, useContext, useEffect, useCallback, useRef } from 'react'
import _ from 'lodash'
import Select from 'react-select'
import { useFormContext, Controller } from 'react-hook-form'

import { BotContext, useFlowContext } from '../../../../contexts'
import api from '../../../../../api'
import { useSelector } from 'react-redux'
import { getBotFlows } from 'Selectors/bots'

function getSelectedStep(flow, stepId) {
  const selectedStep = _.get(flow, `conversation.steps.${stepId}`)

  if (!selectedStep) {
    return null
  }

  const isStartingStep = _.get(flow, 'conversation.starting_step_id') === stepId

  return {
    label: isStartingStep ? "Bot's first step" : selectedStep.name,
    value: stepId
  }
}

function getSelectefFlow(flows, flowId) {
  const selectedFlow = flows.find((flow) => flow.id === flowId)

  if (!selectedFlow) {
    return null
  }

  return {
    value: selectedFlow.id,
    label: selectedFlow.settings?.name ? selectedFlow.settings?.name : 'Default'
  }
}

const FlowSelect = ({ actionGroup, name = 'action' }) => {
  const { id: flowId } = useFlowContext()
  const flowSelectorName = `${name}.${actionGroup ? actionGroup + '.' : ''}flowId`
  const stepSelectorName = `${name}.${actionGroup ? actionGroup + '.' : ''}step`

  const flowAbortController = useRef(null)
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const bot = useContext(BotContext)
  const {
    control,
    formState: { errors },
    watch,
    setValue
  } = useFormContext()

  const [currentFlow, setCurrentFlow] = useState({})
  const [isStepsFetching, setIsStepsFetching] = useState(true)

  const allFlows = useSelector((state) => getBotFlows(state, bot.id))

  const currentSelectedFlowId = watch(flowSelectorName)

  const flowSelectOptions = useMemo(() => {
    return allFlows
      .slice()
      .reverse()
      .filter((flow) => flow.id !== flowId)
      .map((flow) => {
        const choice = {
          value: flow.id
        }

        choice.label = flow.settings?.name ? flow.settings.name : 'Default'

        return choice
      })
  }, [allFlows, flowId])

  const stepSelectOptions = useMemo(() => {
    const choices = []
    const steps = _.get(currentFlow, 'conversation.steps')

    _.forIn(steps, (step, stepId) => {
      const isStartingStep = _.get(currentFlow, 'conversation.starting_step_id') === stepId

      choices.push({
        label: isStartingStep ? "Bot's first step" : step.name,
        value: stepId
      })
    })

    return choices
  }, [currentFlow])

  const loadFlow = useCallback(async (botId, flowId) => {
    setIsStepsFetching(true)
    const controller = new AbortController()
    flowAbortController.current = controller

    try {
      const { data } = await api.get(`/api/v1/bot-profiles/${botId}/bot-flows/${flowId}`, {
        signal: controller.signal
      })

      setCurrentFlow(data)
    } catch (err) {
      console.error('Error while fetching flow', err)
    }

    if (!isMounted.current) {
      return
    }

    setIsStepsFetching(false)
  }, [])

  useEffect(() => {
    const isValidFlow = allFlows.find((flow) => flow.id === currentSelectedFlowId)

    if (isValidFlow) {
      loadFlow(bot.id, currentSelectedFlowId)
    }

    return () => {
      if (flowAbortController.current) {
        flowAbortController.current.abort()
      }
    }
  }, [bot, allFlows, currentSelectedFlowId, loadFlow])

  const stepError = _.get(errors, stepSelectorName)
  const flowError = _.get(errors, flowSelectorName)

  return (
    <Fragment>
      <div className='select-action mb-2'>
        <div className='label'>Choose a Bot</div>
        <Controller
          name={flowSelectorName}
          control={control}
          shouldUnregister
          rules={{
            required: {
              value: true,
              message: 'Bot is required'
            }
          }}
          render={({ field: { value, onChange } }) => {
            return (
              <Select
                menuPlacement='auto'
                menuPortalTarget={document.body}
                options={flowSelectOptions}
                classNamePrefix='react_select'
                value={getSelectefFlow(allFlows, value)}
                onChange={(option) => {
                  onChange(option.value)
                  setValue(stepSelectorName, null)
                }}
              />
            )
          }}
        />
        {flowError && <small className='form-text text-danger'>{flowError.message}</small>}
      </div>
      {currentSelectedFlowId && (
        <div className='select-action mb-2'>
          <div className='label'>What is the next step?</div>
          <Controller
            name={stepSelectorName}
            control={control}
            shouldUnregister
            rules={{
              required: {
                value: true,
                message: 'Step is required'
              }
            }}
            render={({ field: { value, onChange } }) => {
              return (
                <Select
                  menuPlacement='auto'
                  menuPortalTarget={document.body}
                  options={stepSelectOptions}
                  classNamePrefix='react_select'
                  isLoading={isStepsFetching}
                  value={getSelectedStep(currentFlow, value)}
                  onChange={(option) => {
                    onChange(option.value)
                  }}
                />
              )
            }}
          />
          {stepError && <small className='form-text text-danger'>{stepError.message}</small>}
        </div>
      )}
    </Fragment>
  )
}

export default FlowSelect
